// The message-channel catalog is a small, fixed, frontend-owned list (unlike the
// server-driven integrations catalog). WhatsApp + Telegram are self-serve via the
// existing guided modals; Slack/Discord aren't self-serve yet, so they're
// "request access" (records demand + opens a support email). Telegram leads and
// is flagged recommended.

export type ChannelMode = "self-serve" | "request";

export type MessageChannelDef = {
  // Catalog slug; matches the channel value on the wire AND the integrations
  // catalog slug, so the strip can borrow the brand logo from the fetched
  // catalog instead of shipping its own asset.
  slug: string;
  name: string;
  // One-line context shown on the card.
  description: string;
  mode: ChannelMode;
  recommended?: boolean;
  // Which guided setup modal a self-serve channel opens.
  setup?: "whatsapp" | "telegram" | "msteams";
};

const ALL_CHANNEL_SLUGS: ReadonlyArray<string> = [
  "telegram",
  "whatsapp",
  "microsoft-teams",
  "slack",
  "discord",
];

// Which channels each plan tier includes, by tier_definition.tier_code.
// Presentation-level guidance for the onboarding wizard, not enforcement -
// the backend doesn't gate channel setup on tier. Deliberately FAIL-OPEN:
// a null tier (no invite / admin invite without one) or a tier_code not in
// this map means no filtering, so a customer is never under-served by
// missing data. Decided 2026-07-17: Teams/Slack/Discord are pro-only.
// Decided 2026-07-21: the trades tiers (tier-1/2/3) include every channel.
export const TIER_CHANNEL_SLUGS: Readonly<Record<string, ReadonlyArray<string>>> = {
  explore: ["telegram"],
  accelerate: ["telegram", "whatsapp"],
  pro: ALL_CHANNEL_SLUGS,
  "tier-1": ALL_CHANNEL_SLUGS,
  "tier-2": ALL_CHANNEL_SLUGS,
  "tier-3": ALL_CHANNEL_SLUGS,
};

export const channelIncludedInTier = (tier: string | null | undefined, slug: string): boolean => {
  if (!tier) return true;
  const included = TIER_CHANNEL_SLUGS[tier];
  if (!included) return true;
  return included.includes(slug);
};

export const MESSAGE_CHANNELS: ReadonlyArray<MessageChannelDef> = [
  {
    slug: "telegram",
    name: "Telegram",
    description: "Create a Telegram bot and chat with your agent there. The fastest channel to set up.",
    mode: "self-serve",
    recommended: true,
    setup: "telegram",
  },
  {
    slug: "whatsapp",
    name: "WhatsApp",
    description: "Give your agent its own WhatsApp number and message it like any other contact.",
    mode: "self-serve",
    setup: "whatsapp",
  },
  {
    slug: "microsoft-teams",
    name: "Microsoft Teams",
    description: "Add your agent to Microsoft Teams for your organisation.",
    mode: "self-serve",
    setup: "msteams",
  },
  {
    slug: "slack",
    name: "Slack",
    description: "Bring your agent into your Slack workspace so your whole team can talk to it.",
    mode: "request",
  },
  {
    slug: "discord",
    name: "Discord",
    description: "Run your agent in your Discord server for your community.",
    mode: "request",
  },
];
